
import React from 'react';
import { ChevronLeft, Play, Pause, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Bot, BotStatus } from '@/types/trader';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';

interface PageHeaderProps {
  bot: Bot | null | undefined;
  isLoading: boolean;
  onBack: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

export const PageHeader = ({ 
  bot, 
  isLoading, 
  onBack,
  onToggleStatus,
  onDelete,
  isDeleting
}: PageHeaderProps) => {
  const { t } = useTranslation();
  const statusStyles = {
    active: 'bg-green-500/20 text-green-500 border-green-500/30',
    inactive: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={onBack}
          >
            <ChevronLeft size={16} />
            <span>{t('back', 'Back')}</span>
          </Button>
          
          {isLoading ? (
            <Skeleton className="h-8 w-40" />
          ) : (
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{bot?.name || t('bot_information')}</h1>
              {bot && (
                <Badge variant="outline" className={`font-normal ml-2 ${bot.status ? statusStyles[bot.status] : ''}`}>
                  {bot.status === 'active' && (
                    <span className="active-dot mr-1.5">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                  )}
                  {bot.status?.charAt(0).toUpperCase() + bot.status?.slice(1) || t('unknown', 'Unknown')}
                </Badge>
              )}
            </div>
          )}
        </div>
        
        {!isLoading && bot && (
          <div className="flex gap-2">
            {bot.status === 'active' ? (
              <Button 
                variant="outline"
                onClick={onToggleStatus}
              >
                <Pause size={16} className="mr-2" />
                {t('pause_bot', 'Pause Bot')}
              </Button>
            ) : (
              <Button 
                className="bg-primary hover:bg-primary/90"
                onClick={onToggleStatus}
              >
                <Play size={16} className="mr-2" />
                {t('start_bot', 'Start Bot')}
              </Button>
            )}
            
            <Button 
              variant="destructive"
              onClick={onDelete}
              disabled={isDeleting}
            >
              <Trash2 size={16} className="mr-2" />
              {t('delete_bot', 'Delete Bot')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
